import { createSlice } from '@reduxjs/toolkit';

import { getRecruitmentURL } from '../api/commonHTTP';
import { DEFAULT_PAGE_LOAD_SIZE } from '../api/pagination';
import { getEmptyPaginationState } from '../utils/objectUtils';

import { apiCallBegan, apiVersion } from './api';

const slice = createSlice({
    name: 'recruitmentService',
    initialState: {
        businessAuditData: [],
        businessContactCardAuditData: [],
        auditVacancyField: [],
        businesses: [],
        businessesMetaData: {},
        businessesByCodeOrName: [],
        businessesByLoggedInUser: [],
        businessesByLoggedInUserMetaData: getEmptyPaginationState(),
        businessesByOtherUsers: [],
        businessesByOwner: [],
        businessesByOwnerMetaData: getEmptyPaginationState(),
        poolApplications: [],
        poolApplicationsForOwners: [],
        placements: [],
        pools: [],
        poolsForOwners: [],
        recruitmentParticipantTimelineEvents: [],
        recruitmentParticipantVacancyTimelineEvents: [],
        vacancies: [],
        vacanciesMetaData: {},
        vacanciesByDistance: [],
        vacanciesByDistanceMetaData: {},
        vacanciesForServices: [],
        vacanciesForServicesMetaData: {},
        vacancySubmissions: [],
        vacancySubmissionsForBusiness: [],
        vacancySubmissionsForOtherBusiness: [],
        vacancySubmissionsForVacancy: [],
        currentBusiness: {},
        configureContractPlacement: {},
        currentPlacement: {},
        currentPool: {},
        currentPoolApplication: {},
        currentVacancy: {},

        loadingBusinessAuditData: false,
        loadingBusinessContactCardAuditData: false,
        loadingAuditVacancyField: false,
        loadingBusiness: false,
        loadingBusinesses: false,
        loadingBusinessesByCodeOrName: false,
        loadingBusinessesByLoggedInUser: false,
        loadingBusinessesByOwner: false,
        loadingConfigureContractPlacement: false,
        loadingRecruitmentParticipantTimeline: false,
        loadingParticipantVacancyTimeline: false,
        loadingPoolApplication: false,
        loadingPoolApplications: false,
        loadingPoolApplicationsForOwners: false,
        loadingPlacement: false,
        loadingPlacements: false,
        loadingPool: false,
        loadingPools: false,
        loadingPoolsForOwners: false,
        loadingVacancies: false,
        loadingVacanciesByDistance: false,
        loadingVacanciesForServices: false,
        loadingVacancy: false,
        loadingVacancySubmissions: false,
        loadingVacancySubmissionsForBusiness: false,
        loadingVacancySubmissionsForOtherBusiness: false,
        loadingVacancySubmissionsForVacancy: false,

        successMessage: '',
        errorMessage: '',
        validationErrors: {},
        version: {}
    },
    reducers: {
        clearBusinesses: (state) => {
            state.businesses = [];
            state.businessesMetaData = getEmptyPaginationState();
        },

        clearPlacements: (state) => {
            state.placements = [];
        },

        clearPoolApplicationsForOwners: (state) => {
            state.poolApplicationsForOwners = [];
        },

        clearPoolsForOwners: (state) => {
            state.poolsForOwners = [];
        },

        clearBusinessAuditData: (state) => {
            state.businessAuditData = [];
        },

        clearBusinessContactCardAuditData: (state) => {
            state.businessContactCardAuditData = [];
        },

        clearAuditVacancyField: (state) => {
            state.auditVacancyField = [];
        },

        clearVacancies: (state) => {
            state.vacancies = [];
            state.vacanciesMetaData = {};
        },

        clearVacancySubmissions: (state) => {
            state.vacancySubmissions = [];
        },

        // AUDIT
        businessAuditDataLoading: (state) => {
            state.loadingBusinessAuditData = true;
        },
        businessAuditDataLoaded: (state, action) => {
            state.loadingBusinessAuditData = false;
            state.businessAuditData = action.payload;
        },
        businessAuditDataFailed: (state, action) => {
            state.loadingBusinessAuditData = false;
            state.errorMessage = action.payload;
        },

        businessContactCardAuditDataLoading: (state) => {
            state.loadingBusinessContactCardAuditData = true;
        },
        businessContactCardAuditDataLoaded: (state, action) => {
            state.loadingBusinessContactCardAuditData = false;
            state.businessContactCardAuditData = action.payload;
        },
        businessContactCardAuditDataFailed: (state, action) => {
            state.loadingBusinessContactCardAuditData = false;
            state.errorMessage = action.payload;
        },

        auditVacancyFieldLoading: (state) => {
            state.loadingAuditVacancyField = true;
        },
        auditVacancyFieldLoaded: (state, action) => {
            state.loadingAuditVacancyField = false;
            state.auditVacancyField = action.payload;
        },
        auditVacancyFieldFailed: (state, action) => {
            state.loadingAuditVacancyField = false;
            state.errorMessage = action.payload;
        },

        // BUSINESS
        businessCreatedForLoggedInUser: (state, action) => {
            state.currentBusiness = action.payload;
            state.businesses = [...state.businesses, action.payload];

            state.businessesByLoggedInUser = [...state.businessesByLoggedInUser, action.payload];
            state.businessesByLoggedInUserMetaData = {
                ...state.businessesByLoggedInUserMetaData,
                totalElements: state.businessesByLoggedInUserMetaData.totalElements + 1,
                numberOfElements: state.businessesByLoggedInUserMetaData.numberOfElements + 1
            };
        },

        businessCreatedForOtherUser: (state, action) => {
            state.currentBusiness = action.payload;
            state.businesses = [...state.businesses, action.payload];
        },

        businessUpdatedForLoggedInUser: (state, action) => {
            state.loadingBusiness = false;
            const businesses = state.businessesByLoggedInUser.filter(
                (el) => el.id !== action.payload.id
            );
            state.businessesByLoggedInUser = [...businesses, action.payload];
            state.currentBusiness = action.payload;
        },

        businessUpdatedForOtherUser: (state, action) => {
            state.loadingBusiness = false;
            state.businessesByLoggedInUser = state.businessesByLoggedInUser?.filter(
                (el) => el.id !== action.payload.id
            );
            state.currentBusiness = action.payload;
        },

        businessDeleted: (state, action) => {
            state.businessesByLoggedInUser = state.businessesByLoggedInUser.map((el) =>
                el.id === action.payload.id ? { ...el, inactive: true } : el
            );
        },

        businessesByCodeOrNameLoaded: (state, action) => {
            state.loadingBusinessesByCodeOrName = false;
            state.businessesByCodeOrName = action.payload.content;
        },

        businessesByCodeOrNameLoading: (state) => {
            state.loadingBusinessesByCodeOrName = true;
            state.businessesByCodeOrName = [];
        },

        businessesByCodeOrNameLoadingFailed: (state, action) => {
            state.loadingBusinessesByCodeOrName = false;
            state.errorMessage = action.payload;
        },

        businessesByLoggedInUserLoaded: (state, action) => {
            state.loadingBusinessesByLoggedInUser = false;
            const { content, ...rest } = action.payload;
            state.businessesByLoggedInUser = rest.first
                ? content
                : [...state.businessesByLoggedInUser, ...content];
            state.businessesByLoggedInUserMetaData = rest;
        },

        businessesByLoggedInUserLoading: (state) => {
            state.loadingBusinessesByLoggedInUser = true;
        },

        businessesByLoggedInUserLoadingFailed: (state, action) => {
            state.loadingBusinessesByLoggedInUser = false;
            state.errorMessage = action.payload;
        },

        businessesByOwnerLoaded: (state, action) => {
            state.loadingBusinessesByOwner = false;
            const { content, ...rest } = action.payload;
            state.businessesByOwner = rest.first
                ? content
                : [...state.businessesByOwner, ...content];
            state.businessesByOwnerMetaData = rest;
        },

        businessesByOwnerLoading: (state) => {
            state.loadingBusinessesByOwner = true;
        },

        businessesByOwnerLoadingFailed: (state, action) => {
            state.loadingBusinessesByOwner = false;
            state.errorMessage = action.payload;
        },

        businessesLoaded: (state, action) => {
            state.loadingBusinesses = false;
            const { content, ...rest } = action.payload;
            state.businesses = rest.first ? content : [...state.businesses, ...content];
            state.businessesMetaData = rest;
        },

        businessesLoading: (state) => {
            state.loadingBusinesses = true;
        },

        businessesLoadingFailed: (state, action) => {
            state.loadingBusinesses = false;
            state.errorMessage = action.payload;
        },

        businessLoaded: (state, action) => {
            state.loadingBusiness = false;
            state.currentBusiness = action.payload;
        },

        businessLoading: (state) => {
            state.loadingBusiness = true;
            state.currentBusiness = {};
        },

        businessLoadingFailed: (state, action) => {
            state.loadingBusiness = false;
            state.errorMessage = action.payload;
        },

        setCurrentBusiness: (state, action) => {
            state.currentBusiness = action.payload;
        },

        // PLACEMENT
        placementLoading: (state) => {
            state.loadingPlacement = true;
            state.currentPlacement = {};
        },

        placementLoadingFailed: (state, action) => {
            state.loadingPlacement = false;
            state.errorMessage = action.payload;
        },

        placementsLoaded: (state, action) => {
            state.loadingPlacements = false;
            state.placements = action.payload;
        },

        placementsLoading: (state) => {
            state.loadingPlacements = true;
            state.placements = [];
        },

        placementsLoadingFailed: (state, action) => {
            state.loadingPlacements = false;
            state.errorMessage = action.payload;
        },

        placementCreated: (state, action) => {
            state.currentPlacement = action.payload;
            state.placements = [...state.placements, action.payload];
        },

        placementUpdated: (state, action) => {
            state.loadingPlacement = false;
            const placements = state.placements.filter((el) => el.id !== action.payload.id);
            state.placements = [...placements, action.payload];
            state.currentPlacement = action.payload;
        },

        configureContractPlacementLoaded: (state, action) => {
            state.loadingConfigureContractPlacement = false;
            state.configureContractPlacement = action.payload === '' ? {} : action.payload;
        },

        configureContractPlacementLoading: (state) => {
            state.loadingConfigureContractPlacement = true;
            state.configureContractPlacement = {};
        },

        configureContractPlacementLoadingFailed: (state, action) => {
            state.loadingConfigureContractPlacement = false;
            state.errorMessage = action.payload;
        },

        configureContractPlacementUpdated: (state, action) => {
            state.loadingConfigureContractPlacement = false;
            state.configureContractPlacement = action.payload;
        },

        // POOLING
        // Can be multiple pools created
        poolApplicationCreated: (state, action) => {
            state.currentPoolApplication = action.payload[0];
            const poolApplications = state.poolApplications.filter(
                (el) =>
                    !action.payload.some((ppId) => ppId.participantPoolId === el.participantPoolId)
            );
            state.poolApplications = [...poolApplications, ...action.payload];
        },

        poolApplicationLoaded: (state, action) => {
            state.loadingPoolApplication = false;
            state.currentPoolApplication = action.payload;
        },

        poolApplicationLoading: (state) => {
            state.loadingPoolApplication = true;
            state.currentPoolApplication = {};
        },

        poolApplicationLoadingFailed: (state, action) => {
            state.loadingPoolApplication = false;
            state.errorMessage = action.payload;
        },

        poolApplicationsForOwnersLoaded: (state, action) => {
            state.loadingPoolApplicationsForOwners = false;
            state.poolApplicationsForOwners = action.payload;
        },

        poolApplicationsForOwnersLoading: (state) => {
            state.loadingPoolApplicationsForOwners = true;
            state.poolApplicationsForOwners = [];
        },

        poolApplicationsForOwnersLoadingFailed: (state, action) => {
            state.loadingPoolApplicationsForOwners = false;
            state.errorMessage = action.payload;
        },

        poolApplicationsLoaded: (state, action) => {
            state.loadingPoolApplications = false;
            state.poolApplications = action.payload;
        },

        poolApplicationsLoading: (state) => {
            state.loadingPoolApplications = true;
            state.poolApplications = [];
        },

        poolApplicationsLoadingFailed: (state, action) => {
            state.loadingPoolApplications = false;
            state.errorMessage = action.payload;
        },

        poolApplicationsUpdated: (state, action) => {
            state.loadingPoolApplications = false;
            if (Array.isArray(action.payload)) {
                state.poolApplications = state.poolApplications?.map(
                    (el) => action.payload.find((entry) => entry.id === el.id) || el
                );
            } else {
                state.poolApplications = state.poolApplications.map((el) =>
                    el.id === action.payload.id ? action.payload : el
                );
            }
        },

        poolLoading: (state) => {
            state.loadingPool = true;
            state.currentPool = {};
        },

        poolLoadingFailed: (state, action) => {
            state.loadingPool = false;
            state.errorMessage = action.payload;
        },

        poolsForOwnersLoaded: (state, action) => {
            state.loadingPoolsForOwners = false;
            state.poolsForOwners = action.payload;
        },

        poolsForOwnersLoading: (state) => {
            state.loadingPoolsForOwners = true;
            state.poolsForOwners = [];
        },

        poolsForOwnersLoadingFailed: (state, action) => {
            state.loadingPoolsForOwners = false;
            state.errorMessage = action.payload;
        },

        poolsLoaded: (state, action) => {
            state.loadingPools = false;
            state.pools = action.payload;
        },

        poolsLoading: (state) => {
            state.loadingPools = true;
            state.pools = [];
        },

        poolsLoadingFailed: (state, action) => {
            state.loadingPools = false;
            state.errorMessage = action.payload;
        },

        poolCreated: (state, action) => {
            state.currentPool = action.payload;
            state.pools = [...state.pools, action.payload];
        },

        setCurrentPool: (state, action) => {
            state.currentpool = action.payload;
        },

        // TIMELINE
        recruitmentParticipantTimelineEventsLoaded: (state, action) => {
            state.loadingRecruitmentParticipantTimeline = false;
            state.recruitmentParticipantTimelineEvents = action.payload;
        },

        recruitmentParticipantTimelineEventsLoading: (state) => {
            state.loadingRecruitmentParticipantTimeline = true;
            state.recruitmentParticipantTimelineEvents = [];
        },

        recruitmentParticipantTimelineEventsLoadingFailed: (state, action) => {
            state.loadingRecruitmentParticipantTimeline = false;
            state.errorMessage = action.payload;
        },

        // VACANCY
        vacanciesByDistanceLoading: (state) => {
            state.loadingVacanciesByDistance = true;
        },

        vacanciesByDistanceLoaded: (state, action) => {
            state.loadingVacanciesByDistance = false;
            const { content, ...rest } = action.payload;
            state.vacanciesByDistance = rest.first
                ? content
                : [...state.vacanciesByDistance, ...content];
            state.vacanciesByDistanceMetaData = rest;
        },

        vacanciesByDistanceLoadingFailed: (state, action) => {
            state.loadingVacanciesByDistance = false;
            state.errorMessage = action.payload;
        },

        vacanciesForServicesLoaded: (state, action) => {
            state.loadingVacanciesForServices = false;
            const { content, ...rest } = action.payload;
            state.vacanciesForServices = rest.first
                ? content
                : [...state.vacanciesForServices, ...content];
            state.vacanciesForServicesMetaData = rest;
        },

        vacanciesForServicesLoading: (state) => {
            state.loadingVacanciesForServices = true;
        },

        vacanciesForServicesLoadingFailed: (state, action) => {
            state.loadingVacanciesForServices = false;
            state.errorMessage = action.payload;
        },

        vacanciesLoading: (state) => {
            state.loadingVacancies = true;
        },

        vacanciesLoaded: (state, action) => {
            state.loadingVacancies = false;
            const { content, ...rest } = action.payload;
            content.forEach((el, i, arr) => {
                const id = el.businessRecordId;
                if (!id) return;
                state.vacancies = {
                    ...state.vacancies,
                    [id]:
                        state.vacancies[id] && Array.isArray(state.vacancies[id])
                            ? [
                                  ...state.vacancies[id].filter(
                                      (element) =>
                                          !arr.find(
                                              (entry, idx) => idx >= i && entry.id === element.id
                                          )
                                  ),
                                  el
                              ]
                            : [el]
                };
            });
            state.vacanciesMetaData = rest;
        },

        vacanciesLoadingFailed: (state, action) => {
            state.loadingVacancies = false;
            state.errorMessage = action.payload;
        },

        vacancyDeleted: (state, action) => {
            state.vacanciesForServices = state.vacanciesForServices.filter(
                (el) => el.id !== action.payload.id
            );
        },

        vacancyLoaded: (state, action) => {
            state.loadingVacancy = false;
            const id = action.payload?.businessRecordId;
            if (!id) return;
            state.vacancies = {
                ...state.vacancies,
                [id]:
                    state.vacancies[id] && Array.isArray(state.vacancies[id])
                        ? [
                              ...state.vacancies[id].filter((el) => el.id !== action.payload.id),
                              action.payload
                          ]
                        : [action.payload]
            };
            state.currentVacancy = action.payload;
            // TODO Remove next line if not required
            // state.vacanciesForServices = [];
        },

        vacancyLoading: (state) => {
            state.loadingVacancy = true;
            state.currentVacancy = {};
        },

        vacancyLoadingFailed: (state, action) => {
            state.loadingVacancy = false;
            state.errorMessage = action.payload;
        },

        vacancyUpdated: (state, action) => {
            state.loadingVacanciesForServices = false;
            const vacanciesForServices = state.vacanciesForServices.filter(
                (el) => el.id !== action.payload.id
            );
            state.vacanciesForServices = [...vacanciesForServices, action.payload];
            state.currentVacancy = action.payload;
        },

        setCurrentVacancy: (state, action) => {
            state.currentVacancy = action.payload;
        },

        // Can be multiple vacancy submissions created
        vacancySubmissionsCreated: (state, action) => {
            const vacancySubmissions = state.vacancySubmissions.filter(
                (el) => !action.payload.some((ptId) => ptId.participantId === el.participantId)
            );
            state.vacancySubmissions = [...vacancySubmissions, ...action.payload];
        },

        vacancySubmissionsForBusinessLoaded: (state, action) => {
            state.loadingVacancySubmissionsForBusiness = false;
            state.vacancySubmissionsForBusiness = action.payload;
        },

        vacancySubmissionsForBusinessLoading: (state) => {
            state.loadingVacancySubmissionsForBusiness = true;
            state.vacancySubmissionsForBusiness = [];
        },

        vacancySubmissionsForBusinessLoadingFailed: (state, action) => {
            state.loadingVacancySubmissionsForBusiness = false;
            state.errorMessage = action.payload;
        },

        vacancySubmissionsForOtherBusinessLoaded: (state, action) => {
            state.loadingVacancySubmissionsForOtherBusiness = false;
            state.vacancySubmissionsForOtherBusiness = action.payload;
        },

        vacancySubmissionsForOtherBusinessLoading: (state) => {
            state.loadingVacancySubmissionsForOtherBusiness = true;
            state.vacancySubmissionsForOtherBusiness = [];
        },

        vacancySubmissionsForOtherBusinessLoadingFailed: (state, action) => {
            state.loadingVacancySubmissionsForOtherBusiness = false;
            state.errorMessage = action.payload;
        },

        vacancySubmissionsForVacancyLoaded: (state, action) => {
            state.loadingVacancySubmissionsForVacancy = false;
            state.vacancySubmissionsForVacancy = action.payload;
        },

        vacancySubmissionsForVacancyLoading: (state) => {
            state.loadingVacancySubmissionsForVacancy = true;
            state.vacancySubmissionsForVacancy = [];
        },

        vacancySubmissionsForVacancyLoadingFailed: (state, action) => {
            state.loadingVacancySubmissionsForVacancy = false;
            state.errorMessage = action.payload;
        },

        vacancySubmissionsLoaded: (state, action) => {
            state.loadingVacancySubmissions = false;
            state.vacancySubmissions = action.payload;
        },

        vacancySubmissionsLoading: (state) => {
            state.loadingVacancySubmissions = true;
            state.vacancySubmissions = [];
        },

        vacancySubmissionsLoadingFailed: (state, action) => {
            state.loadingVacancySubmissions = false;
            state.errorMessage = action.payload;
        },

        vacancySubmissionsUpdated: (state, action) => {
            state.loadingVacancySubmissions = false;
            if (Array.isArray(action.payload)) {
                state.vacancySubmissions = state.vacancySubmissions?.map(
                    (el) => action.payload.find((entry) => entry.id === el.id) || el
                );
            } else {
                state.vacancySubmissions = state.vacancySubmissions.map((el) =>
                    el.id === action.payload.id ? action.payload : el
                );
            }
        },

        // MISCELLANEOUS
        errorMessageSet: (state, action) => {
            state.errorMessage = action.payload;
            state.successMessage = '';
        },

        successMessageSet: (state, action) => {
            state.successMessage = action.payload;
            state.errorMessage = '';
        },

        validationErrorOccurred: (state, action) => {
            state.validationErrors = Object.entries(action.error).reduce(
                (acc, [key, value]) => ({
                    ...acc,
                    [key]: { error: true, message: value }
                }),
                {}
            );
        },
        clearValidationErrors: (state) => {
            state.validationErrors = {};
        },

        versionLoaded: (state, action) => {
            state.version = action.payload;
        }
    }
});

// ActionCreators. These are commands rather
// than events that have happened

// AUDIT
export const loadBusinessAuditData = (id, field) => {
    const encodedField = encodeURIComponent(field);
    return apiCallBegan({
        url: getRecruitmentURL() + `rest/business-record/audit/${id}/${encodedField}`,
        onStart: businessAuditDataLoading.type,
        onSuccess: businessAuditDataLoaded.type,
        successMessage: `Audit entry has been searched`,
        onError: businessAuditDataFailed.type
    });
};

export const loadBusinessContactCardAuditData = (id, field) => {
    const encodedField = encodeURIComponent(field);
    return apiCallBegan({
        url: getRecruitmentURL() + `rest/business-record/contact-card/audit/${id}/${encodedField}`,
        onStart: businessContactCardAuditDataLoading.type,
        onSuccess: businessContactCardAuditDataLoaded.type,
        successMessage: `Audit entry has been searched`,
        onError: businessContactCardAuditDataFailed.type
    });
};

export const loadVacancyAuditData = (id, field) => {
    const encodedField = encodeURIComponent(field);
    return apiCallBegan({
        url: getRecruitmentURL() + `rest/vacancy/audit/${id}/${encodedField}`,
        onStart: auditVacancyFieldLoading.type,
        onSuccess: auditVacancyFieldLoaded.type,
        successMessage: `Audit entry has been searched`,
        onError: auditVacancyFieldFailed.type
    });
};

// BUSINESS
export const createBusinessRecord = (data, isLoggedInUser) =>
    apiCallBegan({
        url: getRecruitmentURL() + 'rest/business-record',
        method: 'post',
        data,
        onSuccess: isLoggedInUser
            ? businessCreatedForLoggedInUser.type
            : businessCreatedForOtherUser.type,
        successMessage: `Business record has been added`,
        onError: businessLoadingFailed.type,
        onValidationError: validationErrorOccurred.type
    });

export const deleteBusinessRecord = (id) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/business-record/${id}`,
        method: 'delete',
        onSuccess: businessDeleted.type,
        successMessage: `Business record has been deleted`,
        onError: businessesLoadingFailed.type
    });

export const loadBusinessRecord = (id) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/business-record/${id}`,
        onStart: businessLoading.type,
        onSuccess: businessLoaded.type,
        onError: businessLoadingFailed.type
    });

// fetches ALL business records in the system apart from inactive
export const loadBusinessRecords = (
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'name',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getRecruitmentURL() +
            `rest/business-record?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        onStart: businessesLoading.type,
        onSuccess: businessesLoaded.type,
        successMessage: `Business records have been loaded`,
        onError: businessesLoadingFailed.type
    });

export const updateBusinessRecord = (data, isLoggedInUser) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/business-record/${data.id}`,
        method: 'put',
        data,
        onStart: businessLoading.type,
        onSuccess: isLoggedInUser
            ? businessUpdatedForLoggedInUser.type
            : businessUpdatedForOtherUser.type,
        successMessage: `Business record has been updated`,
        onError: businessLoadingFailed.type
    });

export const searchBusinessesByCodeOrName = (data) =>
    apiCallBegan({
        url: getRecruitmentURL() + 'rest/business-record/search',
        method: 'post',
        data,
        onStart: businessesByCodeOrNameLoading.type,
        onSuccess: businessesByCodeOrNameLoaded.type,
        successMessage: `Business search record has been loaded`,
        onError: businessesByCodeOrNameLoadingFailed.type
    });

export const searchBusinessesByLoggedInUser = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'name',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getRecruitmentURL() +
            `rest/business-record/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: businessesByLoggedInUserLoading.type,
        onSuccess: businessesByLoggedInUserLoaded.type,
        successMessage: `Business records by logged in user have been loaded`,
        onError: businessesByLoggedInUserLoadingFailed.type
    });

export const searchBusinessesByOwner = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'code',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getRecruitmentURL() +
            `rest/business-record/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: businessesByOwnerLoading.type,
        onSuccess: businessesByOwnerLoaded.type,
        onError: businessesByOwnerLoadingFailed.type
    });

// Configure Placement
export const loadConfigureContractPlacement = (contractId) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/contract-configuration/${contractId}`,
        onStart: configureContractPlacementLoading.type,
        onSuccess: configureContractPlacementLoaded.type,
        onError: configureContractPlacementLoadingFailed.type
    });

export const updateConfigureContractPlacement = (data) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/contract-configuration/${data.id}`,
        method: 'put',
        data,
        onStart: configureContractPlacementLoading.type,
        onSuccess: configureContractPlacementUpdated.type,
        successMessage: `Placement has been updated`,
        onError: configureContractPlacementLoadingFailed.type
    });

// Placement
export const createPlacement = (data, msg) =>
    apiCallBegan({
        url: getRecruitmentURL() + 'rest/placement',
        method: 'post',
        data,
        onStart: placementLoading.type,
        onSuccess: placementCreated.type,
        successMessage: msg,
        onError: placementLoadingFailed.type,
        onValidationError: validationErrorOccurred.type
    });

export const loadPlacementsForParticipant = (participantId) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/placement/participant/${participantId}`,
        onStart: placementsLoading.type,
        onSuccess: placementsLoaded.type,
        successMessage: `Job placements have been loaded`,
        onError: placementsLoadingFailed.type
    });

export const updatePlacement = (data, msg) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/placement/${data.id}`,
        method: 'put',
        data,
        onStart: placementLoading.type,
        onSuccess: placementUpdated.type,
        successMessage: msg,
        onError: placementLoadingFailed.type,
        onValidationError: validationErrorOccurred.type
    });

// Pooling
export const createPool = (data) =>
    apiCallBegan({
        url: getRecruitmentURL() + 'rest/participant-pool',
        method: 'post',
        data,
        onSuccess: poolCreated.type,
        successMessage: `Pool has been added`,
        onError: poolLoadingFailed.type
    });

export const createPoolApplication = (data) =>
    apiCallBegan({
        url: getRecruitmentURL() + 'rest/participant-pool/application/request',
        method: 'post',
        data,
        onSuccess: poolApplicationCreated.type,
        successMessage: `Pool application(s) have been added`,
        onError: poolApplicationLoadingFailed.type
    });

export const updatePoolApplicationAccepted = (poolApplicationId) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/participant-pool/application/accept/${poolApplicationId}`,
        method: 'put',
        onSuccess: poolApplicationsUpdated.type,
        successMessage: `Pool applications accepted have been updated`,
        onError: poolApplicationsLoadingFailed.type
    });

export const updatePoolApplicationRejected = (data) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/participant-pool/application/reject`,
        method: 'put',
        data,
        onSuccess: poolApplicationsUpdated.type,
        successMessage: `Pool application rejected has been updated`,
        onError: poolApplicationsLoadingFailed.type
    });

export const loadPoolApplicationsByParticipant = (id) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/participant-pool/application/search`,
        method: 'post',
        data: { participantId: id },
        onStart: poolApplicationsLoading.type,
        onSuccess: poolApplicationsLoaded.type,
        successMessage: `Pool applications have been loaded`,
        onError: poolApplicationsLoadingFailed.type
    });

export const loadPoolApplicationsByPoolIds = (poolIds) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/participant-pool/application/search`,
        method: 'post',
        data: { participantPoolIds: poolIds },
        onStart: poolApplicationsLoading.type,
        onSuccess: poolApplicationsLoaded.type,
        successMessage: `Pool applications have been loaded`,
        onError: poolApplicationsLoadingFailed.type
    });

export const loadPoolApplicationsForOwnersByPoolIds = (poolIds) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/participant-pool/application/search`,
        method: 'post',
        data: { participantPoolIds: poolIds },
        onStart: poolApplicationsForOwnersLoading.type,
        onSuccess: poolApplicationsForOwnersLoaded.type,
        successMessage: `Pool applications for owners have been loaded`,
        onError: poolApplicationsForOwnersLoadingFailed.type
    });

export const updatePoolApplicationsAccepted = (data) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/participant-pool/application/accept`,
        method: 'put',
        data,
        onSuccess: poolApplicationsUpdated.type,
        successMessage: `Pool applications accepted have been updated`,
        onError: poolApplicationsLoadingFailed.type
    });

export const loadPoolsByJobSector = (id) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/participant-pool/search`,
        method: 'post',
        data: { jobSectorId: id },
        onStart: poolsLoading.type,
        onSuccess: poolsLoaded.type,
        onError: poolsLoadingFailed.type
    });

export const loadPoolsByUser = (id) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/participant-pool/search`,
        method: 'post',
        data: { ownerId: id },
        onStart: poolsLoading.type,
        onSuccess: poolsLoaded.type,
        onError: poolsLoadingFailed.type
    });

export const loadPoolsForOwnersByUser = (id) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/participant-pool/search`,
        method: 'post',
        data: { ownerId: id },
        onStart: poolsForOwnersLoading.type,
        onSuccess: poolsForOwnersLoaded.type,
        onError: poolsForOwnersLoadingFailed.type
    });

export const loadPoolsForApplications = (poolIds) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/participant-pool/search`,
        method: 'post',
        data: { ids: poolIds },
        onStart: poolsLoading.type,
        onSuccess: poolsLoaded.type,
        onError: poolsLoadingFailed.type
    });

// TIMELINE
export const loadRecruitmentParticipantTimelineEvent = (participantId) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/recruitment-timeline/participant/${participantId}`,
        onStart: recruitmentParticipantTimelineEventsLoading.type,
        onSuccess: recruitmentParticipantTimelineEventsLoaded.type,
        successMessage: `Recruitment Participant TimelineEvents have been loaded`,
        onError: recruitmentParticipantTimelineEventsLoadingFailed.type
    });

// VACANCY
export const createVacancy = (data) =>
    apiCallBegan({
        url: getRecruitmentURL() + 'rest/vacancy',
        method: 'post',
        data,
        onStart: vacancyLoading.type,
        onSuccess: vacancyLoaded.type,
        successMessage: `Vacancy has been added`,
        onError: vacancyLoadingFailed.type
    });

export const deleteVacancy = (id) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/vacancy/${id}`,
        method: 'delete',
        onSuccess: vacancyDeleted.type,
        successMessage: `Vacancy record has been deleted`,
        onError: vacanciesLoadingFailed.type
    });

export const loadVacancy = (id) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/vacancy/${id}`,
        onStart: vacancyLoading.type,
        onSuccess: vacancyLoaded.type,
        successMessage: `Vacancy record has been loaded`,
        onError: vacancyLoadingFailed.type
    });

export const searchVacanciesByBusinessRecordId = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'title',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getRecruitmentURL() +
            `rest/vacancy/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: vacanciesLoading.type,
        onSuccess: vacanciesLoaded.type,
        successMessage: `Vacancies have been loaded for business records`,
        onError: vacanciesLoadingFailed.type
    });

export const searchVacanciesByServiceId = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'code',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getRecruitmentURL() +
            `rest/vacancy/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: vacanciesForServicesLoading.type,
        onSuccess: vacanciesForServicesLoaded.type,
        successMessage: `Vacancies have been loaded for services`,
        onError: vacanciesForServicesLoadingFailed.type
    });

export const searchVacanciesByDistance = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'code',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getRecruitmentURL() +
            `rest/vacancy/search/distance?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: vacanciesByDistanceLoading.type,
        onSuccess: vacanciesByDistanceLoaded.type,
        successMessage: `Vacancies have been loaded by distance`,
        onError: vacanciesByDistanceLoadingFailed.type
    });

export const updateVacancy = (data) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/vacancy/${data.id}`,
        method: 'put',
        data,
        onSuccess: vacancyUpdated.type,
        successMessage: `Vacancy has been updated`,
        onError: vacanciesForServicesLoadingFailed.type
    });

// VACANCY SUBMISSIONS
export const createVacancySubmissions = (data, vacancyId, message) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/vacancy-submission/${vacancyId}/submissions`,
        method: 'put',
        data,
        onSuccess: vacancySubmissionsCreated.type,
        successMessage: message,
        onError: vacancyLoadingFailed.type
    });

export const loadVacancySubmissionsByParticipantId = (participantId) =>
    apiCallBegan({
        url: getRecruitmentURL() + 'rest/vacancy-submission/search',
        method: 'post',
        data: { participantId },
        onStart: vacancySubmissionsLoading.type,
        onSuccess: vacancySubmissionsLoaded.type,
        successMessage: `Vacancy submissions have been loaded for participant id: ${participantId}`,
        onError: vacancySubmissionsLoadingFailed.type
    });

export const loadVacancySubmissionsByVacancyId = (vacancyId) =>
    apiCallBegan({
        url: getRecruitmentURL() + 'rest/vacancy-submission/search',
        method: 'post',
        data: { vacancyId },
        onStart: vacancySubmissionsLoading.type,
        onSuccess: vacancySubmissionsLoaded.type,
        successMessage: `Vacancy submissions have been loaded for vacancy id: ${vacancyId}`,
        onError: vacancySubmissionsLoadingFailed.type
    });

export const loadVacancySubmissionsByVacancyIdForBusiness = (vacancyId, businessId) =>
    apiCallBegan({
        url: getRecruitmentURL() + 'rest/vacancy-submission/search',
        method: 'post',
        data: { vacancyId },
        onStart: vacancySubmissionsForBusinessLoading.type,
        onSuccess: vacancySubmissionsForBusinessLoaded.type,
        successMessage: `Vacancy submissions have been loaded for vacancy id: ${vacancyId} and business id: ${businessId}`,
        onError: vacancySubmissionsForBusinessLoadingFailed.type
    });

export const loadVacancySubmissionsByVacancyIdForOtherBusiness = (vacancyId, businessId) =>
    apiCallBegan({
        url: getRecruitmentURL() + 'rest/vacancy-submission/search',
        method: 'post',
        data: { vacancyId },
        onStart: vacancySubmissionsForOtherBusinessLoading.type,
        onSuccess: vacancySubmissionsForOtherBusinessLoaded.type,
        successMessage: `Vacancy submissions have been loaded for vacancy id: ${vacancyId} and other business id: ${businessId}`,
        onError: vacancySubmissionsForOtherBusinessLoadingFailed.type
    });

export const loadVacancySubmissionsByVacancyIdForVacancy = (vacancyId) =>
    apiCallBegan({
        url: getRecruitmentURL() + 'rest/vacancy-submission/search',
        method: 'post',
        data: { vacancyId },
        onStart: vacancySubmissionsForVacancyLoading.type,
        onSuccess: vacancySubmissionsForVacancyLoaded.type,
        successMessage: `Vacancy submissions for vacancy have been loaded vacancy id: ${vacancyId}`,
        onError: vacancySubmissionsForVacancyLoadingFailed.type
    });

export const updateVacancySubmissionRejected = (data) =>
    apiCallBegan({
        url: getRecruitmentURL() + `rest/vacancy-submission/reject`,
        method: 'post',
        data,
        onSuccess: vacancySubmissionsUpdated.type,
        successMessage: `Vacancy submission has been rejected`,
        onError: vacancySubmissionsLoadingFailed.type
    });

export const loadRecruitmentServiceVersion = () =>
    apiVersion({
        url: getRecruitmentURL() + 'rest/version',
        method: 'get',
        onSuccess: versionLoaded.type,
        onError: errorMessageSet.type
    });

// Put selectors for getting data from state here
// Selectors take state and return computed state
export const setCurrentlySelectedBusiness = (business) => businessUpdatedForLoggedInUser(business);

// Action reducers called directly without http
export const {
    clearAuditVacancyField,
    clearBusinessAuditData,
    clearBusinessContactCardAuditData,
    clearBusinesses,
    clearValidationErrors,
    clearPlacements,
    clearPoolApplicationsForOwners,
    clearPoolsForOwners,
    clearVacancies,
    clearVacancySubmissions,
    setCurrentBusiness,
    setCurrentVacancy
} = slice.actions;

// Used internally
const {
    businessAuditDataLoading,
    businessAuditDataLoaded,
    businessAuditDataFailed,
    businessContactCardAuditDataLoading,
    businessContactCardAuditDataLoaded,
    businessContactCardAuditDataFailed,
    auditVacancyFieldLoading,
    auditVacancyFieldLoaded,
    auditVacancyFieldFailed,

    businessCreatedForLoggedInUser,
    businessCreatedForOtherUser,
    businessDeleted,
    businessesByCodeOrNameLoading,
    businessesByCodeOrNameLoaded,
    businessesByCodeOrNameLoadingFailed,
    businessesByLoggedInUserLoading,
    businessesByLoggedInUserLoaded,
    businessesByLoggedInUserLoadingFailed,
    businessesByOwnerLoading,
    businessesByOwnerLoaded,
    businessesByOwnerLoadingFailed,
    businessesLoading,
    businessesLoaded,
    businessesLoadingFailed,
    businessLoading,
    businessLoaded,
    businessLoadingFailed,
    businessUpdatedForLoggedInUser,
    businessUpdatedForOtherUser,
    configureContractPlacementLoaded,
    configureContractPlacementLoading,
    configureContractPlacementLoadingFailed,
    configureContractPlacementUpdated,
    errorMessageSet,
    placementCreated,
    placementLoading,
    placementUpdated,
    placementLoadingFailed,
    placementsLoading,
    placementsLoaded,
    placementsLoadingFailed,
    poolApplicationCreated,
    poolApplicationsForOwnersLoading,
    poolApplicationsForOwnersLoaded,
    poolApplicationsForOwnersLoadingFailed,
    poolApplicationLoadingFailed,
    poolApplicationsLoaded,
    poolApplicationsLoading,
    poolApplicationsLoadingFailed,
    poolApplicationsUpdated,
    poolCreated,
    poolLoadingFailed,
    poolsForOwnersLoading,
    poolsForOwnersLoaded,
    poolsForOwnersLoadingFailed,
    poolsLoaded,
    poolsLoading,
    poolsLoadingFailed,
    recruitmentParticipantTimelineEventsLoaded,
    recruitmentParticipantTimelineEventsLoading,
    recruitmentParticipantTimelineEventsLoadingFailed,
    vacanciesByDistanceLoading,
    vacanciesByDistanceLoaded,
    vacanciesByDistanceLoadingFailed,
    vacanciesForServicesLoading,
    vacanciesForServicesLoaded,
    vacanciesForServicesLoadingFailed,
    vacanciesLoaded,
    vacanciesLoading,
    vacanciesLoadingFailed,
    vacancyDeleted,
    vacancyLoaded,
    vacancyLoading,
    vacancyLoadingFailed,
    vacancySubmissionsCreated,
    vacancySubmissionsForBusinessLoading,
    vacancySubmissionsForBusinessLoaded,
    vacancySubmissionsForBusinessLoadingFailed,
    vacancySubmissionsForOtherBusinessLoading,
    vacancySubmissionsForOtherBusinessLoaded,
    vacancySubmissionsForOtherBusinessLoadingFailed,
    vacancySubmissionsForVacancyLoading,
    vacancySubmissionsForVacancyLoaded,
    vacancySubmissionsForVacancyLoadingFailed,
    vacancySubmissionsLoading,
    vacancySubmissionsLoaded,
    vacancySubmissionsLoadingFailed,
    vacancySubmissionsUpdated,
    vacancyUpdated,
    validationErrorOccurred,
    versionLoaded
} = slice.actions;

export default slice.reducer;
